import type { DatavizGrade, DatavizSeries, DatavizStep } from './model/dataviz.types';

/** 계열 색 → 면 클래스(`color.dataviz.series.N`) */
export const SERIES_BG_CLASS: Record<DatavizSeries, string> = {
  1: 'bg-dataviz-series-1',
  2: 'bg-dataviz-series-2',
  3: 'bg-dataviz-series-3',
  4: 'bg-dataviz-series-4',
  5: 'bg-dataviz-series-5',
  6: 'bg-dataviz-series-6',
  7: 'bg-dataviz-series-7',
  8: 'bg-dataviz-series-8',
  9: 'bg-dataviz-series-9',
  10: 'bg-dataviz-series-10',
};

/** 계열 색 → 선 클래스(`color.dataviz.series.N`) — SVG 선(Sparkline) */
export const SERIES_STROKE_CLASS: Record<DatavizSeries, string> = {
  1: 'stroke-dataviz-series-1',
  2: 'stroke-dataviz-series-2',
  3: 'stroke-dataviz-series-3',
  4: 'stroke-dataviz-series-4',
  5: 'stroke-dataviz-series-5',
  6: 'stroke-dataviz-series-6',
  7: 'stroke-dataviz-series-7',
  8: 'stroke-dataviz-series-8',
  9: 'stroke-dataviz-series-9',
  10: 'stroke-dataviz-series-10',
};

/** 계열 색 → SVG 면 클래스(`color.dataviz.series.N`) — 점 · 막대 · 누적 막대 조각 */
export const SERIES_FILL_CLASS: Record<DatavizSeries, string> = {
  1: 'fill-dataviz-series-1',
  2: 'fill-dataviz-series-2',
  3: 'fill-dataviz-series-3',
  4: 'fill-dataviz-series-4',
  5: 'fill-dataviz-series-5',
  6: 'fill-dataviz-series-6',
  7: 'fill-dataviz-series-7',
  8: 'fill-dataviz-series-8',
  9: 'fill-dataviz-series-9',
  10: 'fill-dataviz-series-10',
};

/** 계열 색 → CSS 변수 — SVG 그라디언트 정지점(`stop-color`)은 클래스로 줄 수 없어 style 로 넘긴다(선 아래 면) */
export const SERIES_COLOR_VAR: Record<DatavizSeries, string> = {
  1: 'var(--color-dataviz-series-1)',
  2: 'var(--color-dataviz-series-2)',
  3: 'var(--color-dataviz-series-3)',
  4: 'var(--color-dataviz-series-4)',
  5: 'var(--color-dataviz-series-5)',
  6: 'var(--color-dataviz-series-6)',
  7: 'var(--color-dataviz-series-7)',
  8: 'var(--color-dataviz-series-8)',
  9: 'var(--color-dataviz-series-9)',
  10: 'var(--color-dataviz-series-10)',
};

/** 등급 색 → 면 클래스(`color.dataviz.grade.N`) */
export const GRADE_BG_CLASS: Record<`${DatavizGrade}`, string> = {
  1: 'bg-dataviz-grade-1',
  2: 'bg-dataviz-grade-2',
  3: 'bg-dataviz-grade-3',
  4: 'bg-dataviz-grade-4',
  none: 'bg-dataviz-grade-none',
};

/** 양수 구간 색 → 면 클래스(`color.dataviz.positive.N`) */
export const POSITIVE_BG_CLASS: Record<DatavizStep, string> = {
  1: 'bg-dataviz-positive-1',
  2: 'bg-dataviz-positive-2',
  3: 'bg-dataviz-positive-3',
  4: 'bg-dataviz-positive-4',
  5: 'bg-dataviz-positive-5',
  6: 'bg-dataviz-positive-6',
  7: 'bg-dataviz-positive-7',
  8: 'bg-dataviz-positive-8',
  9: 'bg-dataviz-positive-9',
  10: 'bg-dataviz-positive-10',
};

/** 음수 구간 색 → 면 클래스(`color.dataviz.negative.N`) */
export const NEGATIVE_BG_CLASS: Record<DatavizStep, string> = {
  1: 'bg-dataviz-negative-1',
  2: 'bg-dataviz-negative-2',
  3: 'bg-dataviz-negative-3',
  4: 'bg-dataviz-negative-4',
  5: 'bg-dataviz-negative-5',
  6: 'bg-dataviz-negative-6',
  7: 'bg-dataviz-negative-7',
  8: 'bg-dataviz-negative-8',
  9: 'bg-dataviz-negative-9',
  10: 'bg-dataviz-negative-10',
};

/**
 * 그래프 자리의 Figma 고정 치수 = `size.chart.*`.
 * TimeSeriesChart 352(위 8 + 그래프 320 + 가로축 글자 24) · StackedBarChart 388(바깥 여백 20 + 막대 300 + 축 글자)
 * · Treemap 664(메뉴 줄 24 + 그래프 640) · Treemap status=empty 400 · ChartTooltip 너비 240 · 줄 영역 최대 높이 100
 */
export const CHART_SIZE_CLASS = {
  timeSeries: 'h-(--size-chart-time-series)',
  stackedBar: 'h-(--size-chart-stacked-bar)',
  treemap: 'h-(--size-chart-treemap)',
  treemapEmpty: 'h-(--size-chart-treemap-empty)',
  tooltip: 'w-(--size-chart-tooltip)',
  tooltipRows: 'max-h-(--size-chart-tooltip-rows)',
} as const;

/**
 * 계산에 쓰는 치수(px) — 값으로 크기를 셈하거나(Heatmap 줄 수) SVG 속성으로 넘긴다(Sparkline).
 */
export const CHART_SIZE = {
  /** Heatmap 그래프 자리: 기간 이름 줄 24 + 줄 높이 × 줄 수 */
  heatmapHeaderHeight: 24,
  /** Heatmap 줄 높이 기본값 */
  heatmapRowHeight: 32,
  /** Treemap 높이(Figma 664 — 메뉴 줄 24 + 그래프 640. 메뉴가 없어 전체를 그래프로 쓴다) */
  treemapHeight: 664,
  /** Sparkline 기본 크기(칸 너비 90 − 10) */
  sparklineWidth: 80,
  sparklineHeight: 50,
} as const;

/**
 * Sparkline 안쪽 여백(px) — 기존 LineGraph 의 margin · Figma `Sparkline` padding(space/1 4 · space/2-5 10)과 같다.
 * SVG 좌표 셈에 쓰므로 숫자로 둔다.
 */
export const SPARKLINE_MARGIN = { top: 4, right: 10, bottom: 10, left: 4 } as const;

/** 그래프를 그리기 전(너비를 재기 전) 쓰는 기본 너비 — 규격 §2 기본 672 */
export const CHART_DEFAULT_WIDTH = 672;

/**
 * TimeSeriesChart 치수(px) — 규격 §2 · §3. 전체 352 = 위 여백 8 + 그래프 영역 320 + 가로축 글자 줄 24.
 * SVG 좌표 셈에 쓰므로 숫자로 둔다.
 */
export const TIME_SERIES_LAYOUT = {
  padTop: 8,
  plotHeight: 320,
  xAxisHeight: 24,
  /** 단위 글자(세로로 눕힘) 칸 너비 */
  unitWidth: 16,
  /** 눈금 글자와 그래프 영역 사이 */
  tickGap: 8,
  barWidth: 10,
  dotRadius: 3,
  /** 올린 기간의 점 */
  activeDotRadius: 4,
  /** 막대 위 값 글자 사이(space/1) */
  valueGap: 4,
  /** 이 개수를 넘으면 좌우로 옮긴다(카드 13 · extend 24) — 한 화면에 보이는 기간 수 */
  visiblePeriods: 13,
  visiblePeriodsExtend: 24,
  /** 끊김 구분선에 올림을 받는 투명 영역 너비 */
  breakHitWidth: 8,
  /** 가로축 글자 사이 최소 여백 — 좁으면 글자를 건너뛴다 */
  xLabelGap: 8,
} as const;

/** StackedBarChart 치수(px) — 높이 388 = 바깥 여백 20 + 막대 이름 줄 24 + 막대 영역 300 + 값 줄 24 + 바깥 여백 20 */
export const STACKED_BAR_LAYOUT = {
  padding: 20,
  axisWidth: 48,
  labelHeight: 24,
  plotHeight: 300,
  valueHeight: 24,
  /** 막대 사이 · 막대 최대 폭(기존 captable: gap 20 · max-w 200) */
  barGap: 20,
  barMaxWidth: 200,
  /** 막대 이름 · 값 글자와 막대 사이 */
  textGap: 6,
} as const;

/** Heatmap 치수(px) */
export const HEATMAP_LAYOUT = {
  /** 줄 이름 칸 너비(청사진 "너비 100") */
  labelWidth: 100,
  /** 칸 최소 너비 — 기간이 많아 이보다 좁아지면 가로로 스크롤한다 */
  minCellWidth: 40,
} as const;

/** Treemap 글자 · 이름표 셈(px) */
/**
 * 트리맵 네모 안 글자 단계 — 위에서부터 들어가는 첫 단계를 쓴다(treemapTextSize). 여기만 고치면 된다.
 * - `fontSize` · `lineHeight`: 들어가는지 잴 때 쓰는 값(px) — `className` 의 글자 규격과 같아야 한다
 * - `lines`: both = 이름 / 값 두 줄, name = 이름 한 줄
 * - `truncate`: true 면 폭이 모자라도 말줄임(…)으로 잘라 쓴다 — 이름 첫 글자 + … 가 들어갈 폭은 있어야 한다.
 *   false 면 다 들어가야만 쓴다
 * 어느 단계도 안 들어가면 글자를 쓰지 않는다(말풍선으로 본다)
 */
export const TREEMAP_TEXT_LEVELS = [
  {
    key: 'large',
    fontSize: 20,
    lineHeight: 28,
    className: 'typo-heading-3',
    lines: 'both',
    truncate: false,
  },
  {
    key: 'small',
    fontSize: 10,
    lineHeight: 14,
    className: 'typo-micro',
    lines: 'both',
    truncate: false,
  },
  {
    key: 'name',
    fontSize: 10,
    lineHeight: 14,
    className: 'typo-micro',
    lines: 'name',
    truncate: true,
  },
] as const satisfies readonly {
  key: string;
  fontSize: number;
  lineHeight: number;
  className: string;
  lines: 'both' | 'name';
  truncate: boolean;
}[];

export type TreemapTextLevel = (typeof TREEMAP_TEXT_LEVELS)[number];

export const TREEMAP_LAYOUT = {
  /** 네모 사이(조각마다 안쪽 1px → 맞닿으면 2px) */
  gap: 2,
  /** 네모 안쪽 여백 */
  inset: 4,
  /** 이름표 높이(caption-strong 16) · 바깥 여백 */
  tagHeight: 16,
  tagOffset: 4,
} as const;

/** 말풍선: 올림이 끝난 뒤 닫기까지 기다리는 시간(ms) — 말풍선으로 마우스를 옮길 틈 */
export const CHART_TOOLTIP_HIDE_DELAY = 120;
